import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AnalystBroNavbar from "../components/AnalystBroNavbar";
import AnalystBroFooter from "../components/AnalystBroFooter";
import { ArrowRight, ShieldCheck, Mail } from "lucide-react";

// Public setup page for the Web MCP connector (plan 282c A5; the Claude directory needs public docs).
// Keep every claim in step with Stock-Screener mcp_server/web_mcp.py (WEB_MCP_TOOLS, credentials view)
// and the per-call caps in web_chart.py / tools_impl.py. No daily quota exists in code today.
const THEME = "ab-light";
const MCP_URL = "https://mcp.analystbro.net/mcp";
const SUPPORT = "mainul@analystbro.net";

const eyebrow = "font-mono text-xs uppercase tracking-[0.12em] text-[var(--ab-accent)]";
const h2 = "font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1]";
const muted = "text-[var(--ab-muted)] leading-relaxed";
const card = "rounded-2xl border border-[var(--ab-border)] p-6 flex flex-col gap-3";
const code = "font-mono text-sm rounded-lg border border-[var(--ab-border)] bg-[var(--ab-bg-alt)] px-3 py-2 break-all";
const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--ab-accent)] px-6 min-h-[48px] font-semibold text-[var(--ab-on-accent)] hover:bg-[var(--ab-accent-hover)] transition";

const TOOLS = [
  ["Find a company", "symbol_search, chart_search: tickers and company names on the DSE and other exchanges."],
  ["Charts and prices", "chart draws a TradingView chart image with the indicators you ask for; chart_bars returns the price bars; chart_metrics computes RSI, distance from the 200 EMA, returns and more for up to 20 stocks."],
  ["Stock screener", "screener_run, screener_schema, screener_values: filter a market by price, volume, valuation, dividends and growth."],
  ["Company filings", "pdf_list, pdf_search, pdf_text, pdf_page: search annual and quarterly reports and open the exact page, with a link to the page image."],
  ["Fundamentals", "fundamentals: five fiscal years (plus TTM) or 20 quarters of income statement, balance sheet, cash flow and ratios, and monthly shareholding for DSE stocks."],
  ["Events and reference data", "events (dividends, record dates, AGMs, earnings dates, splits, IPOs), broker_list, list_datasets, list_dataset_files, and skills (short recipes for common questions)."],
];

const APPS = [
  {
    name: "Claude",
    steps: [
      "Open Settings → Connectors and choose Add custom connector.",
      `Name it AnalystBro and paste ${MCP_URL}.`,
      "If Claude asks for a Client ID and secret (under Advanced settings), paste the ones from AnalystBro Settings → AI apps.",
      "Click Connect, sign in to AnalystBro with your email and the code we send, and click Allow.",
    ],
    note: "Claude's Free plan allows one custom connector.",
  },
  {
    name: "ChatGPT",
    steps: [
      "Open chatgpt.com/plugins, click + and choose Add custom MCP server.",
      `Paste ${MCP_URL} and choose OAuth.`,
      "If ChatGPT asks for a Client ID and secret, paste the ones from AnalystBro Settings → AI apps.",
      "Sign in to AnalystBro and click Allow.",
    ],
  },
  {
    name: "Gemini",
    steps: [
      "Open Customize → Connectors → + Custom → Connect to an MCP server.",
      `Paste ${MCP_URL}. Under Additional settings, paste the Client ID and Client secret from AnalystBro Settings → AI apps.`,
      "Sign in to AnalystBro and click Allow.",
    ],
  },
  {
    name: "Other MCP apps",
    steps: [
      `Claude Code, VS Code, Cursor and other apps that support remote MCP servers with OAuth: add ${MCP_URL} as a Streamable HTTP server.`,
      "If the app can't register itself, enter the Client ID and secret from Settings → AI apps in its config.",
    ],
  },
];

const PROMPTS = [
  "Show Square Pharmaceuticals' revenue and net profit for the last three fiscal years.",
  "Which DSE banks and finance companies have the highest dividend yield right now?",
  "Chart Grameenphone (DSE:GP) weekly with a 50 EMA and RSI.",
  "What did BAT Bangladesh report as profit after tax in its 2024 annual report? Show me the page.",
  "Which DSE companies have a dividend record date or AGM in the next 30 days?",
  "Compare RSI and distance from the 200-day EMA for BATBC, Square Pharma and BRAC Bank.",
  "Show Apple's revenue for the last four quarters.",
];

const LIMITS = [
  "Your AnalystBro plan and limits are the same as in the AnalystBro app.",
  "Per call: chart_bars returns up to 300 bars, chart_metrics takes up to 20 symbols, events returns up to 1,000 events, and the screener pages its results.",
  "Each chart image takes a few seconds to draw. Chart requests from one account wait in their own queue, so many at once are served in turn.",
  "Chart image links stay available for 30 days. Filing PDF links are signed and expire after 72 hours.",
  "Data covers the Dhaka Stock Exchange (Bangladesh) in depth, plus stocks on other exchanges. Coverage of filings and events is what we have collected, so a missing result is not proof that nothing exists.",
];

export default function AiAppsPage() {
  useEffect(() => {
    document.title = "Use AnalystBro in Claude, ChatGPT and Gemini | AnalystBro";
  }, []);

  return (
    <div className={`${THEME} min-h-screen flex flex-col font-sans antialiased`}>
      <AnalystBroNavbar theme={THEME} />

      <main className="flex-1">
        <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-16 md:pt-28 pb-12 flex flex-col gap-6">
          <span className={eyebrow}>AI apps · MCP connector</span>
          <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
            Use AnalystBro in Claude, ChatGPT and Gemini
          </h1>
          <p className={`${muted} text-lg max-w-3xl`}>
            AnalystBro is a connector (a remote MCP server) that gives your AI chat app market data: charts and
            price bars from TradingView, a stock screener, company filings, fundamentals and a corporate events
            calendar. It covers the Dhaka Stock Exchange in depth and stocks on other exchanges too.
          </p>
          <p className="flex items-start gap-2 text-[var(--ab-muted)] max-w-3xl">
            <ShieldCheck size={20} className="shrink-0 mt-0.5 text-[var(--ab-accent)]" />
            <span>
              Read-only. The connector looks up market data and draws charts. It can't place trades, and it
              doesn't read or change your watchlists, alerts or portfolios. You need a free AnalystBro account.
            </span>
          </p>
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--ab-faint)]">Server address</span>
            <code className={`${code} w-fit`}>{MCP_URL}</code>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/settings?tab=ai-apps" className={btnPrimary}>
              Settings → AI apps <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-10">
          <div className="flex flex-col gap-3 max-w-2xl">
            <span className={eyebrow}>What it does</span>
            <h2 className={h2}>18 read-only tools</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOOLS.map(([t, b]) => (
              <div key={t} className={card}>
                <h3 className="font-bold text-lg">{t}</h3>
                <p className={muted}>{b}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-10">
          <div className="flex flex-col gap-3 max-w-2xl">
            <span className={eyebrow}>Set it up</span>
            <h2 className={h2}>Add the connector</h2>
            <p className={muted}>
              Your Client ID and secret, if your app asks for them, are in{" "}
              <Link to="/settings?tab=ai-apps" className="font-semibold text-[var(--ab-accent)] hover:underline">
                Settings → AI apps
              </Link>
              . One Client ID and secret works in every app. Keep the secret private.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {APPS.map(({ name, steps, note }) => (
              <div key={name} className={card}>
                <h3 className="font-bold text-lg">{name}</h3>
                <ol className="list-decimal pl-5 flex flex-col gap-1.5 text-[var(--ab-muted)] leading-relaxed">
                  {steps.map((s) => <li key={s} className="break-words">{s}</li>)}
                </ol>
                {note && <p className="text-sm text-[var(--ab-faint)]">{note}</p>}
              </div>
            ))}
          </div>
          <p className={`${muted} max-w-3xl`}>
            App menus change from time to time. If a step doesn't match what you see, look for “connectors” or
            “MCP” in the app's settings.
          </p>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-5">
          <span className={eyebrow}>Try asking</span>
          <h2 className={h2}>Example prompts</h2>
          <ul className="flex flex-col gap-3">
            {PROMPTS.map((p) => (
              <li key={p} className="rounded-xl border border-[var(--ab-border)] px-4 py-3">{p}</li>
            ))}
          </ul>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-5">
          <span className={eyebrow}>Limits</span>
          <h2 className={h2}>What to expect</h2>
          <ul className="list-disc pl-5 flex flex-col gap-2 text-[var(--ab-muted)] leading-relaxed">
            {LIMITS.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-20 flex flex-col gap-5">
          <span className={eyebrow}>Privacy and access</span>
          <h2 className={h2}>Disconnect or revoke access</h2>
          <ul className="list-disc pl-5 flex flex-col gap-2 text-[var(--ab-muted)] leading-relaxed">
            <li>
              To stop one app using AnalystBro, open Settings → AI apps on analystbro.net and click Disconnect next
              to that app under Connected apps. Its access ends at once, whichever way you connected it. You can
              also remove the AnalystBro connector in the app's own settings.
            </li>
            <li>
              To cut off every app that uses your Client ID and secret, click Rotate secret on the same page. Apps
              you want to keep need the new Client ID and secret.
            </li>
            <li>
              What the connector logs, and for how long, is in our{" "}
              <a href="/privacy.html#ai-apps" className="font-semibold text-[var(--ab-accent)] hover:underline">privacy policy</a>.
            </li>
          </ul>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-8 pb-20 md:pb-28 flex flex-col gap-6">
          <p className="flex items-center gap-2 text-[var(--ab-muted)]">
            <Mail size={18} /> Support:{" "}
            <a href={`mailto:${SUPPORT}`} className="font-semibold text-[var(--ab-accent)] hover:underline">{SUPPORT}</a>
          </p>
          <p className="text-sm text-[var(--ab-muted)] leading-relaxed border-t border-[var(--ab-border)] pt-6">
            AnalystBro provides market data and filings for information and research. Nothing it returns is
            investment advice, and AI apps can make mistakes when they read or summarise data.
          </p>
          <div>
            <Link to="/" className="font-semibold text-[var(--ab-accent)] hover:underline">
              ← Back to AnalystBro
            </Link>
          </div>
        </section>
      </main>

      <AnalystBroFooter theme={THEME} />
    </div>
  );
}
